import { getIsCombinationSelected } from './smart-suggestion.helper'
import { Injectable } from '@angular/core'
import { Store } from '@ngxs/store'
import { QuoteMetaState } from 'src/store/quote/quote.meta.state'
import { RemoveWarningAction } from '../smart-suggestion.meta.actions'
import { SmartSuggestionMetaState } from '../smart-suggestion.meta.state'
import {
  IContentSuggestion,
  IWarning,
  ProductTypes,
  WarningActions,
  WarningReferences,
} from '../smart-suggestion.model'
import { Utils } from './../../../helpers/utils.helper'

interface BaseMapped {
  id: string
  title: string
  description: string
  quantity: number
}

interface IMappedProtections extends BaseMapped {
  quantityInStock: number
  supplier: 'ProAuto' | 'Clamper'
  suportedEletricCurrent: number
  portsNumber: number
}

interface IMappedInverters extends BaseMapped {
  panelEletricCurrent: number
  inputs: number
}

/**
 * POLICY: BYD2-664
 * Description: The business rule is defined by task BYD2-664
 */
@Injectable({ providedIn: 'root' })
export class ProtectionsHelper {
  constructor(
    private store: Store,
    private utils: Utils,
  ) {}

  findProtectionsWarningReasons(): IWarning[] {
    const warnings = this.store.selectSnapshot(
      SmartSuggestionMetaState.getWarnings,
    )
    const protectionWarnings = warnings.filter(
      (warning) =>
        warning.reference ===
        WarningReferences.PROTECTION_X_INVERTER_MISSING_REQUIRED_PROTECTIONS,
    )
    return protectionWarnings
  }

  validateProtectionsConstraints() {
    const selectedInverters = this.store.selectSnapshot(
      QuoteMetaState.getSelectedInverters,
    )
    const foundWarnings = this.findProtectionsWarningReasons()

    if (selectedInverters.length === 0) {
      if (foundWarnings.length > 0) {
        this.store.dispatch(new RemoveWarningAction(foundWarnings[0].reference))
      }
      return []
    }

    const selectedProtections = this.store.selectSnapshot(
      QuoteMetaState.getSelectedProtections,
    )
    const someInverterNeedProtection = this.validateNeedOfProtection({
      selectedInverters,
      selectedProtections,
    })

    if (someInverterNeedProtection) {
      const content = `${this.utils.getTranslatedKey(
        'The inverters you selected require protections, please select the necessary protections for your inverters before proceeding to checkout.',
      )}`
      const reason = {
        content,
        isRestrictive: false,
        lockedTabs: [7],
        options: [
          {
            actions: [WarningActions.SEE_SUGGESTIONS],
            title: 'See Suggestions',
          },
        ],
        reference:
          WarningReferences.PROTECTION_X_INVERTER_MISSING_REQUIRED_PROTECTIONS,
      }
      return [reason]
    }

    if (foundWarnings.length > 0) {
      this.store.dispatch(new RemoveWarningAction(foundWarnings[0].reference))
    }

    return []
  }

  getProtectionsSuggestions(): any[] {
    const protections = this.store
      .selectSnapshot(QuoteMetaState.getProtections)
      .map((protection) => ({
        id: protection.id,
        title: protection.title,
        description: protection.description,
        quantity: protection.quantity,
        supplier: protection.supplier,
        suportedEletricCurrent: protection.suportedEletricCurrent,
        portsNumber: protection.portsNumber,
        quantityInStock: protection.quantityInStock,
      }))
      .sort((a, b) => a.portsNumber - b.portsNumber) as IMappedProtections[]

    const inverters = this.store
      .selectSnapshot(QuoteMetaState.getSelectedInverters)
      .map((inverter) => ({
        id: inverter.id,
        title: inverter.title,
        description: inverter.description,
        quantity: inverter.quantity,
        panelEletricCurrent: inverter.panelEletricCurrent,
        inputs: inverter.inputs,
      })) as IMappedInverters[]

    const selectedPanel = this.store.selectSnapshot(
      QuoteMetaState.getSelectedPanel,
    )
    const panelEletricCurrent = selectedPanel?.panelEletricCurrent

    if (inverters.length === 0) {
      return []
    }

    const proAutoProtections = protections.filter(
      (protection) =>
        protection.supplier === 'ProAuto' && protection.quantityInStock > 0,
    )
    const clamperProtections = protections.filter(
      (protection) =>
        protection.supplier === 'Clamper' && protection.quantityInStock > 0,
    )
    const proAutoCombinations = this.getBestProtectionCombination({
      protections: proAutoProtections,
      inverters,
      panelEletricCurrent,
    })
    const clamperCombinations = this.getBestProtectionCombination({
      protections: clamperProtections,
      inverters,
      panelEletricCurrent,
    })

    const proAuto = this.parseOptions(proAutoCombinations)
    const clamper = this.parseOptions(clamperCombinations)

    return [proAuto, clamper].flat()
  }

  private getBestProtectionCombination({
    protections,
    inverters,
    panelEletricCurrent,
  }: {
    protections: IMappedProtections[]
    inverters: IMappedInverters[]
    panelEletricCurrent: number
  }): IMappedProtections[] {
    const combinations: IMappedProtections[][] = []

    for (const inverter of inverters) {
      const combinationsToSingleInverter: IMappedProtections[] = []
      for (const protection of protections) {
        if (panelEletricCurrent <= protection.suportedEletricCurrent) {
          const protectionsToSingleInverter = Math.ceil(
            inverter.inputs / protection.portsNumber,
          )
          combinationsToSingleInverter.push({
            ...protection,
            quantity: inverter.quantity * protectionsToSingleInverter,
          })
        }
      }
      combinations.push(combinationsToSingleInverter)
    }
    if (!combinations.length) {
      return
    }

    let bestCombination: IMappedProtections[] = []

    for (const combination of combinations) {
      let singleBestCombination: IMappedProtections = combination[0]
      for (const item of combination) {
        const isQuantityLess =
          singleBestCombination.id !== item.id &&
          item.quantity < singleBestCombination.quantity
        const isPortsNumberLess =
          singleBestCombination.id !== item.id &&
          item.quantity === singleBestCombination.quantity &&
          item.portsNumber < singleBestCombination.portsNumber
        if (isQuantityLess || isPortsNumberLess) {
          singleBestCombination = item
        }
      }
      if (singleBestCombination?.id) {
        bestCombination.push(singleBestCombination)
      }
    }

    return bestCombination
  }

  private parseOptions(combinations: IMappedProtections[]) {
    if (!combinations.length) return []
    const title = combinations
      .reduce((acc, curr) => {
        acc += `${curr?.quantity}x ${curr?.title}, `
        return acc
      }, '')
      .slice(0, -2)
    const content = combinations.map((protection) => ({
      id: protection?.id,
      quantity: protection?.quantity,
      type: ProductTypes.PROTECTION,
    }))
    const selectedProtections = this.store.selectSnapshot(
      QuoteMetaState.getSelectedProtections,
    )

    return {
      title,
      isSelected: getIsCombinationSelected({
        content,
        products: selectedProtections,
      }),
      content,
    }
  }

  private validateNeedOfProtection({
    selectedProtections,
    selectedInverters,
  }: {
    selectedProtections: any[]
    selectedInverters: any[]
  }): boolean {
    const invertersWhoDependOnProtection = selectedInverters.filter(
      (inverter) => inverter.requiredStringBox,
    )

    if (!invertersWhoDependOnProtection.length) {
      return false
    }

    const invertersQuantity = invertersWhoDependOnProtection.reduce(
      (acc, curr) => {
        acc += curr.quantity
      },
      0,
    )
    const protectionQuantity = selectedProtections.reduce((acc, curr) => {
      acc += curr.quantity
    }, 0)

    return invertersQuantity > protectionQuantity
  }
}
